import moment, { type Moment } from 'moment'
import {
  computed, type ComputedRef, defineComponent, type PropType, ref, type Ref, nextTick,
} from 'vue'
import { type CalendarDay, useCalendar, useCalendarTemplate } from '../../composables'

import '../calendar.sass'

moment.locale('ru')

export default defineComponent({
  props: {
    disablePastDates: {
      type: Boolean,
      default: false,
    },
    isShowCalendarInputs: {
      type: Boolean,
      default: true,
    },

    right: {
      type: Boolean,
      default: false,
    },
    verticalPosition: {
      type: String as PropType<'under' | 'over'>,
      default: 'under',
    },
    start: {
      type: Object as PropType<Moment | undefined>,
    },
    end: {
      type: Object as PropType<Moment | undefined>,
    },
    onSetStart: {
      type: Function as PropType<(date: Moment | undefined) => void>,
      required: true,
    },
    onSetEnd: {
      type: Function as PropType<(date: Moment | undefined) => void>,
      required: true,
    },
  },

  setup(props) {
    const isSelected = computed(() => props.start || props.end)

    const startCalendar = useCalendar(moment(), { disablePastDates: props.disablePastDates })
    const endCalendar = useCalendar(moment().add(1, 'month'), { disablePastDates: props.disablePastDates })
    const todayDate = moment().format('DD-MM-YYYY')

    const hoverDay: Ref<Moment | undefined> = ref(undefined)
    const startDate: ComputedRef<Moment | undefined> = computed(() => (props.start ? moment(props.start) : undefined))
    const endDate: ComputedRef<Moment | undefined> = computed(() => (props.end ? moment(props.end) : undefined))

    const setPeriod = (day: CalendarDay) => {
      if (day.disabled) return

      const currentDate: Moment = moment(day.date)

      const setStart = (val?: Moment) => props.onSetStart && props.onSetStart(val)
      const setEnd = (val?: Moment) => props.onSetEnd && props.onSetEnd(val)

      if (startDate.value && endDate.value) {
        setEnd()
        nextTick(() => setStart(currentDate))
      } else if (startDate.value && day.date.isBefore(startDate.value)) {
        setStart(currentDate)
      } else if (!startDate.value) {
        setStart(currentDate)
      } else if (!endDate.value) {
        setEnd(currentDate)
      }
    }

    const clear = (e: MouseEvent) => {
      e.stopPropagation()

      if (props.onSetStart) props.onSetStart(undefined)

      if (props.onSetEnd) props.onSetEnd(undefined)
    }

    const formatDaysForCalendar = (day: CalendarDay) => {
      const cls = []

      if (day.faded || day.disabled) {
        cls.push('faded')
      }

      if (
        (startDate.value && endDate.value && day.date.isBetween(startDate.value, endDate.value))
        || (startDate.value && !endDate.value && hoverDay.value && (day.date.isBetween(startDate.value, hoverDay.value, 'day', '[]')))
      ) cls.push('in-period')

      if (startDate.value && day.date.isSame(startDate.value, 'day')) {
        cls.push('outlined', 'outlined-start')
      }

      if (endDate.value && day.date.isSame(endDate.value, 'day')) {
        cls.push('outlined', 'outlined-end')
      }

      if (todayDate === day.date.format('DD-MM-YYYY')) {
        cls.push('curr-day')
      }

      return (
        <div class={`ark-ui-calendar-day ${cls.join(' ')}`}>
          <div
            onClick={() => setPeriod(day)}
            onMouseenter={() => { hoverDay.value = moment(day.date) }}
            onMouseleave={() => { hoverDay.value = undefined }}
            class='ark-ui-calendar-day-border'
          >
            { day.date.format('D') }
          </div>
        </div>
      )
    }

    return () => {
      const startCalendarDays: JSX.Element[] = startCalendar.days.value.map(formatDaysForCalendar)
      const endCalendarDays: JSX.Element[] = endCalendar.days.value.map(formatDaysForCalendar)

      return (
        <div class={`ark-ui-double-calendar ${props.verticalPosition}`}>
          {props.isShowCalendarInputs && (
            <div class="ark-ui-double-calendar-inputs" style={`${props.right ? 'justify-content: flex-end;' : ''}`}>

              <input type="text" value={startDate.value?.format('DD.MM.YYYY')} placeholder="__.__.____" disabled />
              <div class="ark-ui-double-calendar-inputs-arrow" />
              <input type="text" value={endDate.value?.format('DD.MM.YYYY')} placeholder="__.__.____" disabled />

              { isSelected.value && <div onClick={clear} class="ark-ui-period-calendar-input-head-chips-clear" /> }
            </div>
          )}

          <div class="ark-ui-double-calendar-body">
            { useCalendarTemplate(startCalendar, startCalendarDays) }
            { useCalendarTemplate(endCalendar, endCalendarDays) }
          </div>
        </div>
      )
    }
  },
})
